import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { analyzeFullMetadata, type FullMusicMetadata } from './audio-analysis';

// CRA uses REACT_APP_*; also accept VITE_* if present via process.env polyfill
const supabaseUrl =
  (process.env.REACT_APP_SUPABASE_URL as string | undefined) ||
  (process.env.VITE_SUPABASE_URL as string | undefined) ||
  '';
const supabaseAnonKey =
  (process.env.REACT_APP_SUPABASE_ANON_KEY as string | undefined) ||
  (process.env.VITE_SUPABASE_ANON_KEY as string | undefined) ||
  '';

export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey);

/** Placeholder URL keeps createClient happy when agent env is not configured. */
const clientUrl = supabaseUrl || 'https://placeholder.supabase.co';
const clientKey = supabaseAnonKey || 'public-anon-key';

export const supabase: SupabaseClient = createClient(clientUrl, clientKey, {
  auth: {
    persistSession: false,
    autoRefreshToken: false,
  },
});

const OWNER_KEY = 'pipes.owner_id';
const SESSION_NUMBER_KEY = 'pipes.session_number';
const MODE_KEY = 'pipes.mode';
const TIER_KEY = 'pipes.agent_tier';
const CAP_KEY = 'pipes.agent_capability';
const REVIEWER_KEY = 'pipes.reviewer_name';

export const REVIEWERS = ['Fred Beteille', 'Katie Larson', 'David', 'Chris', 'Jake'] as const;
export type ReviewerName = (typeof REVIEWERS)[number];

export function getStoredReviewer(): ReviewerName {
  if (typeof window === 'undefined') return 'Fred Beteille';
  const v = window.localStorage.getItem(REVIEWER_KEY);
  if (REVIEWERS.includes(v as ReviewerName)) return v as ReviewerName;
  return 'Fred Beteille';
}

export function setStoredReviewer(name: ReviewerName): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(REVIEWER_KEY, name);
}

export type AppMode = 'human' | 'agent';
export type AgentTier = 'minimal' | 'standard' | 'verbose';
export type AgentCapability = 'basic' | 'advanced' | 'expert';

export function getStoredMode(): AppMode {
  if (typeof window === 'undefined') return 'human';
  const v = window.localStorage.getItem(MODE_KEY);
  return v === 'agent' ? 'agent' : 'human';
}

export function getStoredTier(): AgentTier {
  if (typeof window === 'undefined') return 'standard';
  const v = window.localStorage.getItem(TIER_KEY);
  return v === 'minimal' || v === 'verbose' ? v : 'standard';
}

export function getStoredCapability(): AgentCapability {
  if (typeof window === 'undefined') return 'advanced';
  const v = window.localStorage.getItem(CAP_KEY);
  return v === 'basic' || v === 'expert' ? v : 'advanced';
}

export async function persistMode(ownerId: string, mode: AppMode): Promise<void> {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(MODE_KEY, mode);
  }
  if (!ownerId) return;
  await supabase.from('sessions').update({ mode }).eq('owner_id', ownerId);
}

export async function persistTier(ownerId: string, tier: AgentTier): Promise<void> {
  if (typeof window !== 'undefined') window.localStorage.setItem(TIER_KEY, tier);
  if (!ownerId) return;
  await supabase.from('sessions').update({ agent_tier: tier }).eq('owner_id', ownerId);
}

export async function persistCapability(ownerId: string, capability: AgentCapability): Promise<void> {
  if (typeof window !== 'undefined') window.localStorage.setItem(CAP_KEY, capability);
  if (!ownerId) return;
  await supabase.from('sessions').update({ agent_capability: capability }).eq('owner_id', ownerId);
}

export function getOwnerId(): string {
  if (typeof window === 'undefined') return '';
  let id = window.localStorage.getItem(OWNER_KEY);
  if (!id) {
    id = (crypto.randomUUID && crypto.randomUUID()) || fallbackUuid();
    window.localStorage.setItem(OWNER_KEY, id);
  }
  return id;
}

export function formatSessionId(n: number | null): string {
  if (!n || n < 1) return 'MB_HUM_SESSION_ID_------';
  return `MB_HUM_SESSION_ID_${String(n).padStart(6, '0')}-0001`;
}

export async function getOrCreateSessionNumber(ownerId: string): Promise<number | null> {
  if (!ownerId) return null;
  const cached = window.localStorage.getItem(SESSION_NUMBER_KEY);
  if (cached) {
    const n = Number(cached);
    if (Number.isFinite(n) && n > 0) return n;
  }

  if (!isSupabaseConfigured) return null;

  const existing = await supabase
    .from('sessions')
    .select('session_number, mode, agent_tier, agent_capability')
    .eq('owner_id', ownerId)
    .maybeSingle();

  if (existing.data?.session_number) {
    const n = Number(existing.data.session_number);
    window.localStorage.setItem(SESSION_NUMBER_KEY, String(n));
    if (existing.data.mode === 'agent' || existing.data.mode === 'human') {
      window.localStorage.setItem(MODE_KEY, existing.data.mode);
    }
    if (existing.data.agent_tier) {
      window.localStorage.setItem(TIER_KEY, existing.data.agent_tier);
    }
    if (existing.data.agent_capability) {
      window.localStorage.setItem(CAP_KEY, existing.data.agent_capability);
    }
    return n;
  }

  const inserted = await supabase
    .from('sessions')
    .insert({ owner_id: ownerId })
    .select('session_number')
    .maybeSingle();

  if (inserted.data?.session_number) {
    const n = Number(inserted.data.session_number);
    window.localStorage.setItem(SESSION_NUMBER_KEY, String(n));
    return n;
  }

  return null;
}

function fallbackUuid() {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

// ─── Stem Separation Settings ────────────────────────────────────────────────

const STEM_AUTO_KEY = 'pipes.stem_auto_separate';
const STEM_MODEL_KEY = 'pipes.stem_model';
const STEM_COUNT_KEY = 'pipes.stem_count';
const AGENT_PROVIDER_KEY = 'pipes.agent_provider';
const AGENT_PROVIDER_URL_KEY = 'pipes.agent_provider_url';

export type StemModelPref = 'htdemucs_6s' | 'htdemucs' | 'htdemucs_ft' | 'mdx_extra' | 'demucs_quantized' | 'openunmix';

export interface StemModelInfo {
  id: StemModelPref;
  label: string;
  maxStems: number;
  stemLabels: string[];
  description: string;
}

export const STEM_MODELS: StemModelInfo[] = [
  { id: 'htdemucs_6s', label: 'HTDemucs 6-Stem', maxStems: 6, stemLabels: ['Vocals', 'Drums', 'Bass', 'Guitar', 'Piano', 'Other'], description: '6-source fine-tuned hybrid transformer (best quality)' },
  { id: 'htdemucs', label: 'HTDemucs 4-Stem', maxStems: 4, stemLabels: ['Vocals', 'Drums', 'Bass', 'Other'], description: 'Standard 4-source hybrid transformer' },
  { id: 'htdemucs_ft', label: 'HTDemucs Fine-Tuned', maxStems: 6, stemLabels: ['Vocals', 'Drums', 'Bass', 'Guitar', 'Piano', 'Other'], description: 'Fine-tuned variant with improved vocal isolation' },
  { id: 'mdx_extra', label: 'MDX-Net Extra', maxStems: 4, stemLabels: ['Vocals', 'Drums', 'Bass', 'Other'], description: 'Frequency-domain model, fast inference' },
  { id: 'demucs_quantized', label: 'Demucs Quantized', maxStems: 4, stemLabels: ['Vocals', 'Drums', 'Bass', 'Other'], description: 'Lightweight quantized model for faster processing' },
  { id: 'openunmix', label: 'Open-Unmix', maxStems: 4, stemLabels: ['Vocals', 'Drums', 'Bass', 'Other'], description: 'Open-source LSTM baseline (fastest, lower quality)' },
];

export type AgentProvider = 'openclaw' | 'hermes' | 'ii-agent' | 'custom';

export interface AgentProviderInfo {
  id: AgentProvider;
  label: string;
  description: string;
  defaultUrl: string;
}

export const AGENT_PROVIDERS: AgentProviderInfo[] = [
  { id: 'openclaw', label: 'OpenClaw / Open AI', description: 'OpenAI-compatible agent runtime with function calling', defaultUrl: 'https://api.openai.com/v1' },
  { id: 'hermes', label: 'Hermes', description: 'NousResearch Hermes fine-tuned reasoning agent', defaultUrl: 'https://hermes.nous.ai/v1' },
  { id: 'ii-agent', label: 'ii-Agent', description: 'Intelligent Infrastructure autonomous agent framework', defaultUrl: 'https://api.ii-agent.dev/v1' },
  { id: 'custom', label: 'Custom Agent', description: 'Manually configure an agent endpoint URL', defaultUrl: '' },
];

export function getStemAutoSeparate(): boolean {
  if (typeof window === 'undefined') return true;
  return window.localStorage.getItem(STEM_AUTO_KEY) !== 'false';
}

export function setStemAutoSeparate(val: boolean): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STEM_AUTO_KEY, String(val));
}

export function getStemModel(): StemModelPref {
  if (typeof window === 'undefined') return 'htdemucs_6s';
  const v = window.localStorage.getItem(STEM_MODEL_KEY);
  const valid = STEM_MODELS.map(m => m.id);
  return valid.includes(v as StemModelPref) ? (v as StemModelPref) : 'htdemucs_6s';
}

export function setStemModel(model: StemModelPref): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STEM_MODEL_KEY, model);
}

export function getStemCount(): number {
  if (typeof window === 'undefined') return 6;
  const v = Number(window.localStorage.getItem(STEM_COUNT_KEY));
  return v >= 1 && v <= 20 ? v : getModelInfo(getStemModel()).maxStems;
}

export function setStemCount(count: number): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STEM_COUNT_KEY, String(Math.max(1, Math.min(20, count))));
}

export function getModelInfo(model: StemModelPref): StemModelInfo {
  return STEM_MODELS.find(m => m.id === model) ?? STEM_MODELS[0];
}

export function getAgentProvider(): AgentProvider {
  if (typeof window === 'undefined') return 'openclaw';
  const v = window.localStorage.getItem(AGENT_PROVIDER_KEY);
  const valid = AGENT_PROVIDERS.map(p => p.id);
  return valid.includes(v as AgentProvider) ? (v as AgentProvider) : 'openclaw';
}

export function setAgentProvider(provider: AgentProvider): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(AGENT_PROVIDER_KEY, provider);
}

export function getAgentProviderUrl(): string {
  if (typeof window === 'undefined') return '';
  return window.localStorage.getItem(AGENT_PROVIDER_URL_KEY) ?? '';
}

export function setAgentProviderUrl(url: string): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(AGENT_PROVIDER_URL_KEY, url);
}

async function getTrackAudioUrl(trackId: string): Promise<string | null> {
  const { data } = await supabase.from('tracks').select('audio_url').eq('id', trackId).maybeSingle();
  return data?.audio_url || null;
}

export async function ensureTrackInDb(track: {
  id: string;
  owner_id: string;
  title: string;
  artist: string;
  album?: string | null;
  audio_url?: string | null;
  status?: string;
  stems_status?: string;
  duration_seconds?: number;
  bpm?: number;
  key_signature?: string | null;
}, ownerIdOverride?: string): Promise<string> {
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(track.id);

  if (isUuid) {
    const { data: existing } = await supabase.from('tracks').select('id').eq('id', track.id).maybeSingle();
    if (existing) return existing.id;
  }

  const effectiveOwner = ownerIdOverride ?? track.owner_id;
  const { data: row, error } = await supabase.from('tracks').insert({
    owner_id: effectiveOwner,
    title: track.title,
    artist: track.artist,
    album: track.album ?? '',
    audio_url: track.audio_url ?? '',
    status: track.status ?? 'draft',
    stems_status: track.stems_status ?? 'none',
    duration_seconds: track.duration_seconds ?? 0,
    bpm: track.bpm ?? 0,
    key_signature: track.key_signature ?? '',
  }).select('id').single();

  if (error || !row) throw new Error(error?.message ?? 'Could not save this track to the catalog. Please check your data and try again.');
  return row.id;
}

export async function requestStemSeparation(ownerId: string, trackId: string, model: StemModelPref = 'htdemucs_6s', audioUrl?: string | null): Promise<{ job_id: string; stems_count: number } | { error: string }> {
  // Run real client-side audio analysis if we have an audio URL
  let clientAnalysis: FullMusicMetadata | null = null;
  const resolvedUrl = audioUrl || await getTrackAudioUrl(trackId);
  if (resolvedUrl) {
    try {
      clientAnalysis = await analyzeFullMetadata(resolvedUrl);
    } catch {
      // Analysis failed — proceed without it; edge function will use fallbacks
    }
  }

  const resp = await supabase.functions.invoke('demucs-separate', {
    body: { track_id: trackId, owner_id: ownerId, model, client_analysis: clientAnalysis },
  });
  if (!resp.error && resp.data) return resp.data as { job_id: string; stems_count: number };

  // Fallback: simulate stem separation locally when edge function is unavailable
  return stubStemSeparation(ownerId, trackId, model, 0, undefined, clientAnalysis);
}

export async function retryStemSeparation(jobId: string, model?: StemModelPref): Promise<{ job_id: string; stems_count: number } | { error: string }> {
  const resp = await supabase.functions.invoke(`demucs-separate/${jobId}/retry`, {
    method: 'POST',
    body: { model },
  });
  if (!resp.error && resp.data) return resp.data as { job_id: string; stems_count: number };

  // Fallback: look up the original job and re-simulate
  const { data: oldJob } = await supabase.from('stem_jobs').select('owner_id, track_id, retry_count').eq('id', jobId).maybeSingle();
  if (!oldJob) return { error: 'This separation job no longer exists. It may have been deleted or expired.' };

  // Delete old stems for this job
  await supabase.from('stems').delete().eq('stem_job_id', jobId);

  // Try real analysis for retry
  const audioUrl = await getTrackAudioUrl(oldJob.track_id);
  let clientAnalysis: FullMusicMetadata | null = null;
  if (audioUrl) {
    try { clientAnalysis = await analyzeFullMetadata(audioUrl); } catch { /* proceed without */ }
  }

  return stubStemSeparation(oldJob.owner_id, oldJob.track_id, model || getStemModel(), (oldJob.retry_count ?? 0) + 1, jobId, clientAnalysis);
}

const STEM_NOTES: Record<string, string> = {
  vocals: 'Primary vocal isolated. Minor bleed from reverb tail detected in low frequencies.',
  drums: 'Percussive elements separated. Includes hi-hats, snare, kick, and toms.',
  bass: 'Bass instrument isolated. Clean separation with minimal harmonic bleed.',
  guitar: 'Guitar elements extracted. Includes both rhythm and lead passages.',
  piano: 'Keyboard/piano separated. May include synth pads sharing frequency range.',
  other: 'Residual audio: elements not classified into primary stems. May contain FX, pads, or background textures.',
};

async function stubStemSeparation(
  ownerId: string,
  trackId: string,
  model: StemModelPref,
  retryCount = 0,
  previousJobId?: string,
  clientAnalysis?: FullMusicMetadata | null,
): Promise<{ job_id: string; stems_count: number } | { error: string }> {
  const stemTypes = model === 'htdemucs_6s'
    ? ['vocals', 'drums', 'bass', 'guitar', 'piano', 'other']
    : ['vocals', 'drums', 'bass', 'other'];

  const { data: job, error: jobErr } = await supabase
    .from('stem_jobs')
    .insert({
      owner_id: ownerId,
      track_id: trackId,
      model,
      status: 'completed',
      progress: 100,
      retry_count: retryCount,
      previous_job_id: previousJobId ?? null,
      callback_token: crypto.randomUUID(),
      started_at: new Date().toISOString(),
      completed_at: new Date().toISOString(),
      ttl_expires_at: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
    })
    .select()
    .single();

  if (jobErr || !job) return { error: jobErr?.message ?? 'Could not start the separation job. The service may be temporarily overloaded — try again shortly.' };

  const stemRows = stemTypes.map((stemType) => ({
    owner_id: ownerId,
    track_id: trackId,
    stem_job_id: job.id,
    stem_type: stemType,
    stem_url: null,
    duration_seconds: clientAnalysis?.duration_seconds ?? null,
    agent_confidence: clientAnalysis ? 0.85 : 0.5,
    agent_rms_db: null,
    agent_peak_db: null,
    agent_spectral_centroid: clientAnalysis?.spectral_centroid ?? null,
    agent_notes: STEM_NOTES[stemType] || 'Stem extracted successfully.',
    quality_tier: clientAnalysis ? 'good' : null,
    quality_basis: clientAnalysis ? 'Client-side Essentia.js analysis available' : null,
  }));

  const { error: stemsErr } = await supabase.from('stems').insert(stemRows);
  if (stemsErr) return { error: `Stem data could not be saved: ${stemsErr.message}` };

  return { job_id: job.id, stems_count: stemTypes.length };
}

export async function requestTranscription(
  ownerId: string,
  trackId: string,
  opts?: { stemId?: string; audioUrl?: string; language?: string },
): Promise<{ job_id: string; word_count: number; mode: string } | { error: string }> {
  const resp = await supabase.functions.invoke('whisper-transcribe', {
    body: {
      track_id: trackId,
      owner_id: ownerId,
      stem_id: opts?.stemId || null,
      audio_url: opts?.audioUrl || null,
      language: opts?.language || null,
    },
  });

  if (!resp.error && resp.data) {
    return resp.data as { job_id: string; word_count: number; mode: string };
  }

  return { error: resp.error?.message || 'Transcription could not start. The audio may be inaccessible or the service is temporarily unavailable.' };
}